import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Grid, 
  Alert, 
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  CircularProgress,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { FileText, Eye, Printer, Calendar, Filter, AlertTriangle } from 'lucide-react';
import { format, isBefore, addMonths } from 'date-fns';

const MaintenanceInvoiceManager = () => {
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [branches, setBranches] = useState([]);
  const [filterType, setFilterType] = useState('user'); // 'user' or 'vehicle'
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [users, setUsers] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    if (selectedBranch) {
      fetchUsers();
      fetchVehicles();
    }
  }, [selectedBranch]);

  useEffect(() => {
    if (selectedBranch && ((filterType === 'user' && selectedUser) || (filterType === 'vehicle' && selectedVehicle))) {
      fetchInvoices();
    }
  }, [selectedBranch, filterType, selectedUser, selectedVehicle]);

  const fetchBranches = async () => {
    try {
      const response = await apiService.getBranches();
      setBranches(response);
    } catch (err) {
      console.error('Error fetching branches:', err);
      setError('Failed to fetch branches');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await apiService.getBranchUsers(selectedBranch);
      setUsers(response);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to fetch users');
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await apiService.getBranchVehicles(selectedBranch);
      setVehicles(response);
    } catch (err) {
      console.error('Error fetching vehicles:', err);
      setError('Failed to fetch vehicles');
    }
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.getMaintenanceInvoices({
        branch: selectedBranch,
        userId: filterType === 'user' ? selectedUser : null,
        vehicleNumber: filterType === 'vehicle' ? selectedVehicle : null,
      });
      
      setInvoices(response);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setError('Failed to fetch maintenance invoices');
      setLoading(false);
    }
  };

  const handleViewInvoice = (invoice) => {
    if (invoice.file_url) {
      window.open(invoice.file_url, '_blank');
    } else {
      setError('Unable to access the invoice file');
    }
  };

  const handlePrintInvoice = async (invoice) => {
    try {
      const printUrl = await apiService.getMaintenanceInvoicePrintUrl(invoice.id);
      const printWindow = window.open(printUrl, '_blank');
      printWindow.print();
    } catch (err) {
      console.error('Error printing invoice:', err);
      setError('Failed to print invoice');
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Maintenance Invoice Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Branch</InputLabel>
            <Select
              value={selectedBranch}
              label="Branch"
              onChange={(e) => {
                setSelectedBranch(e.target.value);
                setSelectedUser('');
                setSelectedVehicle('');
              }}
            >
              {branches.map((branch) => (
                <MenuItem key={branch.name} value={branch.name}>
                  {branch.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Filter By</InputLabel>
            <Select
              value={filterType}
              label="Filter By"
              onChange={(e) => {
                setFilterType(e.target.value);
                setSelectedUser('');
                setSelectedVehicle('');
              }}
            >
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="vehicle">Vehicle</MenuItem>
            </Select>
          </FormControl>

          {filterType === 'user' && (
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>User</InputLabel>
              <Select
                value={selectedUser}
                label="User"
                onChange={(e) => setSelectedUser(e.target.value)}
                disabled={!selectedBranch}
              >
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.username}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {filterType === 'vehicle' && (
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Vehicle</InputLabel>
              <Select
                value={selectedVehicle}
                label="Vehicle"
                onChange={(e) => setSelectedVehicle(e.target.value)}
                disabled={!selectedBranch}
              >
                {vehicles.map((vehicle) => (
                  <MenuItem key={vehicle.number} value={vehicle.number}>
                    {vehicle.number}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Service Provider</TableCell>
                  <TableCell>Work Performed</TableCell>
                  <TableCell>Uploaded By</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.length > 0 ? (
                  invoices.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell>
                        {format(new Date(invoice.created_at), 'MM/dd/yyyy')}
                      </TableCell>
                      <TableCell>{invoice.service_provider}</TableCell>
                      <TableCell>{invoice.work_performed}</TableCell>
                      <TableCell>{invoice.uploaded_by_name}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                          <Tooltip title="View Invoice">
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleViewInvoice(invoice)}
                            >
                              <Eye size={18} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Print Invoice">
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handlePrintInvoice(invoice)}
                            >
                              <Printer size={18} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No maintenance invoices found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default MaintenanceInvoiceManager; 